<template>
  <div class="sh-root">
    <header class="sh-bar">
      <div class="st-wrap sh-row">
        <router-link :to="homeLink" class="sh-brand" aria-label="Dashboard">
          <span class="na-seal sh-seal">N</span>
          <span class="na-wordmark sh-word">News<span>Appify</span></span>
          <span class="sh-role">{{ who }}</span>
        </router-link>

        <nav class="sh-links" aria-label="Staff tools">
          <router-link v-for="l in links" :key="l.to" :to="l.to" class="sh-link" :exact="l.exact">
            <v-icon size="18">{{ l.icon }}</v-icon>{{ l.label }}
          </router-link>
        </nav>

        <div class="sh-user">
          <button type="button" class="sh-user-btn" :aria-expanded="String(menu)" @click="menu = !menu">
            <span class="sh-avatar">{{ initials }}</span>
            <span class="sh-name">{{ name }}</span>
            <v-icon size="18">mdi-chevron-down</v-icon>
          </button>
          <div v-if="menu" class="sh-menu" @click.stop>
            <router-link v-for="l in links" :key="'m' + l.to" :to="l.to" class="sh-menu-item sh-menu-mobile" @click.native="menu = false">
              <v-icon size="18">{{ l.icon }}</v-icon>{{ l.label }}
            </router-link>
            <router-link :to="settingsLink" class="sh-menu-item" @click.native="menu = false"><v-icon size="18">mdi-account-cog-outline</v-icon>Profile &amp; settings</router-link>
            <router-link to="/" class="sh-menu-item" @click.native="menu = false"><v-icon size="18">mdi-web</v-icon>View website</router-link>
            <button type="button" class="sh-menu-item" @click="logout"><v-icon size="18">mdi-logout</v-icon>Log out</button>
          </div>
        </div>
      </div>
    </header>
    <div v-if="menu" class="sh-scrim" @click="menu = false"></div>
  </div>
</template>

<script>
// Top bar for NewsAppify staff and admin tools.
import { auth, db } from '@/firebase'
import { doc, getDoc } from 'firebase/firestore'
import '@/assets/na-staff.css'

// The signed-in person's real role (from users/{uid}.role) decides the
// menu, so an Admin always sees the admin menu — even on pages that
// Staff and Admins share, like App progress or a customer's page.
// Remembered for the session so the bar doesn't flicker between pages.
let cached = { uid: '', role: '', name: '' }

export default {
  name: 'StaffHeader',
  props: { role: { type: String, default: 'Staff' } },
  data() {
    const u = auth.currentUser
    const hit = u && cached.uid === u.uid
    return { menu: false, name: (hit && cached.name) || 'Team member', actualRole: hit ? cached.role : '' }
  },
  computed: {
    // Customer pages always show the customer menu; otherwise trust the account's role.
    who() { return this.role === 'Customer' ? 'Customer' : (this.actualRole === 'Admin' || this.actualRole === 'Staff' ? this.actualRole : this.role) },
    homeLink() { return { Admin: '/admin', Customer: '/dashboard' }[this.who] || '/staff-dashboard' },
    settingsLink() { return { Admin: '/admin-settings', Customer: '/customer-settings' }[this.who] || '/staff-manage-settings' },
    initials() { return this.name.split(/\s+/).map(p => p[0]).join('').slice(0, 2).toUpperCase() || 'NA' },
    links() {
      if (this.who === 'Admin') {
        return [
          { to: '/admin', label: 'Admin', icon: 'mdi-shield-crown-outline', exact: true },
          { to: '/admin/revenue-reports', label: 'Revenue', icon: 'mdi-chart-bar' },
          { to: '/manage-app-progress', label: 'App progress', icon: 'mdi-progress-check' },
          { to: '/admin/templates', label: 'Templates', icon: 'mdi-email-edit-outline' },
          { to: '/staff/blog', label: 'Blog', icon: 'mdi-post-outline' },
        ]
      }
      if (this.who === 'Customer') {
        return [
          { to: '/dashboard', label: 'Dashboard', icon: 'mdi-view-dashboard-outline', exact: true },
          { to: '/billing', label: 'Billing', icon: 'mdi-credit-card-outline' },
          { to: '/customer-settings', label: 'Settings', icon: 'mdi-cog-outline' },
        ]
      }
      return [
        { to: '/staff-dashboard', label: 'Dashboard', icon: 'mdi-view-dashboard-outline', exact: true },
        { to: '/staff-leads', label: 'Leads', icon: 'mdi-account-search-outline' },
        { to: '/staff-onboard-customer', label: 'Onboard', icon: 'mdi-account-plus-outline' },
        { to: '/staff/templates', label: 'Templates', icon: 'mdi-email-edit-outline' },
        { to: '/staff/blog', label: 'Blog', icon: 'mdi-post-outline' },
        { to: '/staff-payments', label: 'Payments', icon: 'mdi-credit-card-outline' },
      ]
    },
  },
  watch: { $route() { this.menu = false } },
  async mounted() {
    const u = auth.currentUser
    if (!u) return
    if (cached.uid === u.uid && cached.role) return
    this.name = u.displayName || u.email || this.name
    try {
      const s = await getDoc(doc(db, 'users', u.uid))
      if (s.exists()) {
        const d = s.data()
        this.name = `${d.firstName || ''} ${d.lastName || ''}`.trim() || d.fullName || d.displayName || this.name
        this.actualRole = d.role || ''
      }
      cached = { uid: u.uid, role: this.actualRole, name: this.name }
    } catch (e) { /* keep defaults */ }
  },
  methods: {
    async logout() {
      this.menu = false
      cached = { uid: '', role: '', name: '' }
      await auth.signOut()
      this.$router.push('/')
    },
  },
}
</script>

<style scoped>
.sh-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 999;
  background: var(--na-navy-deep);
  border-bottom: 3px solid var(--na-brass);
  color: #fff;
}
.sh-row { display: flex; align-items: center; justify-content: space-between; gap: 20px; height: 72px; }
.sh-brand { display: inline-flex; align-items: center; gap: 10px; text-decoration: none; }
.sh-seal { width: 38px; height: 38px; font-size: 1.15rem; background: var(--na-brass); color: var(--na-navy-deep); box-shadow: inset 0 0 0 3px var(--na-brass), inset 0 0 0 4.5px rgba(11, 30, 59, 0.45); }
.sh-seal::after { background: #fff; border-color: var(--na-navy-deep); width: 12px; height: 12px; }
.sh-word { color: #fff; font-size: 1.35rem; }
.sh-word span { color: var(--na-brass); }
.sh-role { padding: 3px 10px; border: 1px solid rgba(184, 146, 90, 0.6); border-radius: 999px; font-size: 0.7rem; font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase; color: var(--na-brass); }

.sh-links { display: flex; align-items: center; gap: 4px; }
.sh-link { display: inline-flex; align-items: center; gap: 7px; padding: 9px 12px; border-radius: 6px; color: rgba(243, 238, 227, 0.75); text-decoration: none; font-weight: 500; font-size: 0.95rem; }
.sh-link .v-icon { color: inherit !important; }
.sh-link:hover { color: #fff; background: rgba(255, 255, 255, 0.06); }
.sh-link.router-link-active { color: #fff; background: rgba(184, 146, 90, 0.18); }

.sh-user { position: relative; }
.sh-user-btn { display: inline-flex; align-items: center; gap: 9px; padding: 5px 10px 5px 5px; border: 1px solid rgba(255, 255, 255, 0.18); border-radius: 999px; background: transparent; color: #fff; cursor: pointer; font-family: var(--na-sans); }
.sh-user-btn .v-icon { color: rgba(255, 255, 255, 0.7) !important; }
.sh-avatar { width: 32px; height: 32px; border-radius: 50%; background: var(--na-paper); color: var(--na-navy); display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 0.82rem; }
.sh-name { font-size: 0.92rem; max-width: 160px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.sh-menu { position: absolute; right: 0; top: calc(100% + 8px); z-index: 1001; min-width: 230px; padding: 6px; background: var(--na-card); border: 1px solid var(--na-rule); border-radius: 10px; box-shadow: 0 24px 50px -20px rgba(11, 30, 59, 0.5); }
.sh-menu-item { display: flex; align-items: center; gap: 10px; width: 100%; padding: 10px 12px; border: 0; border-radius: 7px; background: transparent; color: var(--na-ink); text-decoration: none; font-family: var(--na-sans); font-size: 0.95rem; text-align: left; cursor: pointer; }
.sh-menu-item .v-icon { color: var(--na-brass-text) !important; }
.sh-menu-item:hover { background: #F2EADB; }
.sh-menu-mobile { display: none; }
.sh-scrim { position: fixed; inset: 0; z-index: 998; }

@media (max-width: 1080px) {
  .sh-links { display: none; }
  .sh-menu-mobile { display: flex; }
}
@media (max-width: 520px) {
  .sh-name, .sh-role { display: none; }
}
</style>